import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import './CustomDropdown.scss';

const CustomDropdown = ({
    value,
    onChange,
    loadOptions,
    defaultOptions = [],
    isDisabled = false,
    isClearable = false,
    isSearchable = true,
    placeholder = "Select...",
    menuPlacement = "bottom",
    className = "",
    classNamePrefix = "custom-dropdown",
    cacheOptions = false,
    additional = {}
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [options, setOptions] = useState(defaultOptions);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(additional.page || 0);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });

    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const searchInputRef = useRef(null);

    // Load initial options
    useEffect(() => {
        if (defaultOptions && defaultOptions.length > 0) {
            setOptions(defaultOptions);
        }
    }, [defaultOptions]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchValue("");
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && isSearchable && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen, isSearchable]);

    // Calculate menu position when dropdown opens
    useEffect(() => {
        if (isOpen && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const controlHeight = dropdownRef.current.querySelector(`.${classNamePrefix}__control`)?.offsetHeight || 38;

            setMenuPosition({
                top: menuPlacement === 'top' ? rect.top - 4 : rect.bottom + 4,
                left: rect.left,
                width: rect.width
            });
        }
    }, [isOpen, menuPlacement, classNamePrefix]);

    // Update menu position on scroll when dropdown is open
    useEffect(() => {
        if (!isOpen) return;

        const updatePosition = () => {
            if (dropdownRef.current) {
                const rect = dropdownRef.current.getBoundingClientRect();
                setMenuPosition({
                    top: menuPlacement === 'top' ? rect.top - 4 : rect.bottom + 4,
                    left: rect.left,
                    width: rect.width
                });
            }
        };

        // Add scroll and resize event listeners
        window.addEventListener('scroll', updatePosition, true); // useCapture for better scroll handling
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, menuPlacement, classNamePrefix]);

    // Load options based on search
    const loadOptionsData = async (search, currentOptions) => {
        if (!loadOptions) return;

        setIsLoading(true);
        try {
            const result = await loadOptions(search, currentOptions);
            setOptions(prevOptions => {
                // If it's a new search, replace options
                if (search !== searchValue || currentOptions.length === 0) {
                    return result.options;
                }
                // Otherwise, append for pagination
                return [...prevOptions, ...result.options];
            });
            setHasMore(result.hasMore || false);
        } catch (error) {
            console.error("Error loading options:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const newSearchValue = e.target.value;
        setSearchValue(newSearchValue);
        setOptions([]);
        setPage(0);
        loadOptionsData(newSearchValue, []);
    };

    // Toggle dropdown
    const toggleDropdown = () => {
        if (isDisabled) return;

        if (!isOpen) {
            setIsOpen(true);
            // Load initial options if not already loaded
            if (options.length === 0 && loadOptions) {
                loadOptionsData("", []);
            }
        } else {
            setIsOpen(false);
            setSearchValue("");
        }
    };

    // Handle option selection
    const handleOptionClick = (option) => {
        if (isDisabled) return;
        onChange(option);
        setIsOpen(false);
        setSearchValue("");
    };

    // Handle clear
    const handleClear = (e) => {
        e.stopPropagation();
        onChange(null);
    };

    // Handle scroll for pagination
    const handleMenuScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isBottom = scrollTop + clientHeight >= scrollHeight - 10;

        if (isBottom && hasMore && !isLoading) {
            loadOptionsData(searchValue, options);
        }
    };

    // Filter options based on search (client-side filtering as fallback)
    const filteredOptions = useMemo(() => {
        if (!searchValue || !isSearchable) return options;

        const searchLower = searchValue.toLowerCase();
        return options.filter(option =>
            option.label?.toLowerCase().includes(searchLower)
        );
    }, [options, searchValue, isSearchable]);

    // Get display value
    const displayValue = value?.label || "";
    const hasValue = value && value.value !== null && value.value !== undefined;

    // Render menu content
    const renderMenu = () => {
        if (!isOpen || isDisabled) return null;

        const menuContent = (
            <div
                className={`${classNamePrefix}__menu ${menuPlacement === 'top' ? `${classNamePrefix}__menu--top` : `${classNamePrefix}__menu--bottom`} ${classNamePrefix}__menu--portal`}
                ref={menuRef}
                onScroll={handleMenuScroll}
                style={{
                    position: 'fixed',
                    top: menuPlacement === 'top' ? 'auto' : `${menuPosition.top}px`,
                    bottom: menuPlacement === 'top' ? `${window.innerHeight - menuPosition.top}px` : 'auto',
                    left: `${menuPosition.left}px`,
                    width: `${menuPosition.width}px`
                }}
            >
                <div className={`${classNamePrefix}__menu-list`}>
                    {isLoading && filteredOptions.length === 0 ? (
                        <div className={`${classNamePrefix}__option ${classNamePrefix}__option--is-loading`}>
                            Loading...
                        </div>
                    ) : filteredOptions.length === 0 ? (
                        <div className={`${classNamePrefix}__option ${classNamePrefix}__option--is-empty`}>
                            No options
                        </div>
                    ) : (
                        filteredOptions.map((option) => (
                            <div
                                key={option.value}
                                className={`${classNamePrefix}__option ${
                                    value?.value === option.value
                                        ? `${classNamePrefix}__option--is-selected`
                                        : ''
                                }`}
                                onClick={() => handleOptionClick(option)}
                            >
                                {option.label}
                            </div>
                        ))
                    )}
                    {isLoading && filteredOptions.length > 0 && (
                        <div className={`${classNamePrefix}__option ${classNamePrefix}__option--is-loading`}>
                            Loading more...
                        </div>
                    )}
                </div>
            </div>
        );

        return ReactDOM.createPortal(menuContent, document.body);
    };

    return (
        <>
            <div
                className={`${classNamePrefix} ${className}`}
                ref={dropdownRef}
            >
                <div
                    className={`${classNamePrefix}__control ${isOpen ? `${classNamePrefix}__control--is-focused` : ''} ${isDisabled ? `${classNamePrefix}__control--is-disabled` : ''}`}
                    onClick={toggleDropdown}
                >
                    <div className={`${classNamePrefix}__value-container`}>
                        {!isOpen || !isSearchable ? (
                            <div className={`${classNamePrefix}__single-value`}>
                                {hasValue ? displayValue : <span className={`${classNamePrefix}__placeholder`}>{placeholder}</span>}
                            </div>
                        ) : (
                            <input
                                ref={searchInputRef}
                                type="text"
                                className={`${classNamePrefix}__input`}
                                value={searchValue}
                                onChange={handleSearchChange}
                                onClick={(e) => e.stopPropagation()}
                                placeholder={placeholder}
                            />
                        )}
                    </div>
                    <div className={`${classNamePrefix}__indicators`}>
                        {isClearable && hasValue && !isDisabled && (
                            <div
                                className={`${classNamePrefix}__clear-indicator`}
                                onClick={handleClear}
                            >
                                ×
                            </div>
                        )}
                        <div className={`${classNamePrefix}__dropdown-indicator`}>
                            <i className={`fa-solid ${isOpen ? 'fa-angle-up' : 'fa-angle-down'}`}></i>
                        </div>
                    </div>
                </div>
            </div>
            {renderMenu()}
        </>
    );
};

export default CustomDropdown;